/**
 * Every clinical number the product acts on, in one file, each with the reason it is that number.
 *
 * These are self-management thresholds, not diagnostic criteria: they decide when HeartCoach asks a
 * patient to look again or tells a care team to look at all. They are deliberately conservative,
 * because a threshold that fires is a claim that somebody should do something.
 */

import type { MetricKind } from './types.js';

/** Heart-failure self-care: a 3 lb overnight gain is the classic "call the clinic" line. */
export const WEIGHT_GAIN_DAY_LBS = 3;

/** And 5 lb over a week, which catches the slower creep a single morning misses. */
export const WEIGHT_GAIN_WEEK_LBS = 5;

/**
 * Hypotension. Cardiac patients are actively titrated on ACE inhibitors and beta blockers, so the
 * product has to be able to say "this is too low", not only "this is too high".
 */
export const BP_LOW = { systolic: 90, diastolic: 60 } as const;

/** A fall of this much from the week's own baseline is worth noticing even inside the normal range. */
export const BP_DROP_SYSTOLIC = 20;

/** Resting pulse bands. Under 40 is the only pulse number that escalates on its own. */
export const PULSE_VERY_LOW = 40;
export const PULSE_LOW = 50;
export const PULSE_HIGH = 120;

/** Plausible ranges per metric. A value outside its range is a bad reading, not a bad patient. */
export const METRIC_BOUNDS: Readonly<Record<MetricKind, readonly [number, number]>> = {
  'heart-rate': [25, 250],
  hrv: [1, 300],
  'sleep-hours': [0, 24],
  'weight-lbs': [40, 1000],
  'exertion-minutes': [0, 1440],
};

/** How many days of per-day metric summaries a patient's state carries. Raw samples live in the log. */
export const METRIC_RETENTION_DAYS = 90;

/** How many days of symptom reports the state carries. */
export const SYMPTOM_RETENTION_DAYS = 180;

/** How recent a fact has to be to raise a flag. Older than this, a care team cannot act on it. */
export const FLAG_WINDOW_MS = 48 * 3_600_000;

export function withinBounds(kind: MetricKind, value: number): boolean {
  const [lo, hi] = METRIC_BOUNDS[kind];
  return Number.isFinite(value) && value >= lo && value <= hi;
}
